import { useCallback, useLayoutEffect, useEffect, useState, type CSSProperties, type RefObject } from "react";

// D-O222: the app header clips its own overflow, so a menu drawn inside it never shows in full. A menu is drawn in a portal on
// the page body instead, `position: fixed`, and this hook anchors it under its trigger button (right edges aligned, kept on screen).

const EDGE = 8;

/** Where a fixed panel of `width` px sits under `trigger`; re-measured on open, resize and any scroll while `open`. */
export function useAnchoredPlacement(trigger: RefObject<HTMLElement | null>, open: boolean, width: number): CSSProperties {
  const [place, setPlace] = useState<CSSProperties>({ top: 56, left: EDGE, width });

  const measure = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const w = Math.min(width, Math.max(240, window.innerWidth - EDGE * 2));
    // `left`, not `right`: it does not depend on how wide the containing block is when the page shows a scrollbar gutter.
    const left = Math.min(Math.max(EDGE, rect.right - w), Math.max(EDGE, window.innerWidth - w - EDGE));
    setPlace({ top: Math.round(rect.bottom + 8), left, width: w });
  }, [trigger, width]);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, measure]);

  return place;
}
